<template>
  <StepLayout
    :back="back"
    :save="save"
    :next="next"
    buttonDataEc="envelopes_summary_page"
  >
    <template v-slot:title>
      {{
        isSent
          ? $t('ui.envelopes.create.steps.summary.sentTitle')
          : $t('ui.envelopes.create.steps.summary.title')
      }}
    </template>

    <div class="space-y-base text-black">
      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.name.title')"
        :to="toEdit('EnvelopesEditName')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="edit_name_button"
      >
        {{ envelope.title }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.documents.title')"
        :to="toEditStep('EnvelopesStepDocuments')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="go_to_step_documents_button"
      >
        {{ documentNames }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.parties.title')"
        :to="toEditStep('EnvelopesStepParties')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="go_to_step_parties_button"
      >
        !!PARTIES GO HERE!!
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.fields.title')"
        :to="toEditStep('EnvelopesStepFields')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="go_to_step_fields_button"
      >
        {{
          $tc('ui.envelopes.create.steps.summary.fields.description', numberOfFields, {
            fields: numberOfFields,
          })
        }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.signingSequence.title')"
        :to="toEdit('EnvelopesEditSigningSequence')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="edit_signing_sequence_button"
      >
        {{ $t(`ui.envelopes.create.steps.summary.signingSequence.${signingSequence}`) }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.sharing.title')"
        :to="toEdit('EnvelopesEditSharing')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="edit_sharing_button"
      >
        {{
          $t(`ui.envelopes.create.steps.summary.sharing.${sharingType}`, {
            companyName: company.name,
          })
        }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.reminders.title')"
        :to="toEdit('EnvelopesEditReminders')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="edit_reminders_button"
      >
        {{
          $tc(
            'ui.envelopes.create.steps.summary.reminders.description',
            reminderInterval,
            { interval: reminderInterval },
          )
        }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.expires.title')"
        :to="toEdit('EnvelopesEditExpiry')"
        :disabled="isSent"
        dataEc="envelopes_summary_page"
        dataEt="edit_expiry_button"
      >
        {{ expiresAt }}
      </SummaryItem>
      <hr />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.preview.title')"
        dataEc="envelopes_summary_page"
        dataEt="launch_preview_button"
      >
        <template v-slot:controls>
          <ButtonLink
            to="preview_url"
            target="_blank"
            variant="secondary"
            class="whitespace-nowrap"
            :disabled="isSent"
            dataEc="envelopes_summary_page"
            dataEt="launch_preview_button"
          >
            {{ $t('functional.launchPreview') }}
            <Icon
              name="external-link-alt"
              class="ml-base"
            />
          </ButtonLink>
        </template>
      </SummaryItem>
    </div>
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useGetters } from 'vuex-composition-helpers/dist';

import { longDate } from '@/utils/dates';
import { useEnvelope, useEnvelopesRoutes } from '@/composables/envelopes';
import i18n from '@/locales';

import Icon from '@/components/Icon.vue';
import StepLayout from '@/components/layouts/StepLayout';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import SummaryItem from '@/components/library/summary/SummaryItem';

const route = useRoute();
const router = useRouter();

const { prevRoute } = useEnvelopesRoutes();

const { envelope, documents, isSent } = useEnvelope(route.params.fingerprint);

const { company, authenticatedUser } = useGetters({
  company: 'company/company',
  authenticatedUser: 'user/user',
});

const documentNames = computed(() =>
  documents.value.map(template => template.title).join(', '));

const numberOfFields = 5;

const signingSequence = computed(() =>
  (envelope.value.is_parallel_signing_enabled ? 'parallel' : 'sequential'));
const sharingType = computed(() => {
  if (envelope.value.everyone_permission) {
    return 'everyone';
  }

  if (
    envelope.value.sharing.groups.length === 0 &&
    envelope.value.sharing.users.length === 1 &&
    envelope.value.sharing.users[0].user.id === authenticatedUser.id
  ) {
    return 'private';
  }

  return 'individuals';
});
const expiresAt = computed(() => {
  if (!envelope.value.next_expire) {
    return i18n.t('ui.envelopes.create.steps.summary.expires.noneSet');
  }
  return longDate(envelope.value.next_expire);
});
const reminderInterval = computed(() => envelope.value.auto_remind_hours / 24 || 0);

/** @param {`EnvelopesEdit${string}`} page */
function toEdit(page) {
  return {
    name: page,
  };
}

/** @param {`EnvelopesStep${string}`} page */
function toEditStep(page) {
  return {
    name: page,
    params: {
      fingerprint: envelope.value.fingerprint,
      isEditStep: true,
    },
  };
}

const back = {
  action: () => router.push(prevRoute.value),
};
const next = {
  // @TODO send envelope
  action: () => {},
  label: i18n.t('ui.envelopes.create.steps.summary.send'),
};
const save = {
  action: () => router.push({ name: 'Envelopes' }),
};
</script>
